import clsx from 'clsx';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import CoconutTrees from 'components/svg/coconut_trees.svg';
import type { PlayerInvitee } from 'hooks/referrals/usePlayerInvitesAndInvitees';
import { useMemo } from 'react';
import { getRandomMascotAvatar } from 'utils/dashboard/avatar';
import Crab from '../../svg/refer-a-friend/Crab.svg';
import { AvatarOnBeachTowel } from './AvatarOnBeachTowel';
import {
  FriendIconBubbleContent,
  FriendIconIndex,
  FriendIconPlayer,
  getFriendIconTailPosition,
  isFriendIconInactive,
} from './FriendIconShared';
import styles from './ReferralBeachHeaderFullWidth.module.css';
import { ReferralBeachProgressSection } from './ReferralBeachProgressSection';
import { WhiteSpeechBubble } from './WhiteSpeechBubble';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';

interface FriendIconProps {
  player: FriendIconPlayer | null;
  index: FriendIconIndex;
}

const FriendIcon = ({ player, index }: FriendIconProps) => {
  return (
    <>
      <div
        className={clsx(
          styles.speechBubbleIcon,
          styles[`speechBubbleIcon${index}`],
        )}
      >
        <WhiteSpeechBubble
          empty={!player}
          tailPosition={getFriendIconTailPosition(index)}
        >
          <FriendIconBubbleContent player={player} />
        </WhiteSpeechBubble>
      </div>
      <div className={clsx(styles.friendIcon, styles['friendIcon' + index])}>
        <AvatarOnBeachTowel
          avatarUrl={player?.avatarUrl}
          isEmptySlot={!player}
          towelIndex={index}
          inactive={isFriendIconInactive(player)}
        />
      </div>
    </>
  );
};

export const ReferralBeachHeaderFullWidth = ({
  loading,
  referrerName,
  referrerAvatarUrl,
  referees = [],
}: {
  loading: boolean;
  referrerName?: string;
  referrerAvatarUrl?: string;
  referees?: PlayerInvitee[];
}) => {
  const fallbackAvatar = useMemo(() => getRandomMascotAvatar(), []);

  const visibleReferees = referees.slice(0, 3);
  const refereeSlots = Array.from(
    { length: 3 },
    (_, index) => visibleReferees[index] || null,
  );

  return (
    <div className={styles.outerContainer}>
      <div className={styles.container}>
        <AboutUsSiteContainer>
          <CoconutTrees className={clsx('desktopOnly', styles.coconutTree)} />
          <div className={styles.contentWrapper}>
            <h2 className="aboutUsTypographyHeaderXlarge noMargin">
              Invite up to 3 friends <br /> and win together
            </h2>
            <p className="textAlignCenter noMargin">
              Treat your friends to <strong>50% off </strong> their first month
              and <br className="desktopOnly" /> win whenever they do!
            </p>
          </div>
          <div className={styles.mainContent}>
            <div className={styles.refereeContainer}>
              <Crab className={styles.crab} />
              <AspectRatioContainer useCssVariableSizes>
                {!loading &&
                  refereeSlots.map((referee, index) => (
                    <FriendIcon
                      key={index}
                      player={
                        referee
                          ? {
                              avatarUrl: referee.avatar ?? undefined,
                              name: referee.name,
                              status: referee.status,
                              gracePeriodHoursRemaining:
                                referee.gracePeriodHoursRemaining,
                            }
                          : null
                      }
                      index={(index + 1).toString() as '1' | '2' | '3'}
                    />
                  ))}
                {loading && (
                  <div className={styles.refereesLoading}>
                    <PlainLoadingSpinner />
                  </div>
                )}
              </AspectRatioContainer>
            </div>
            <div className={styles.progressContainer}>
              <ReferralBeachProgressSection
                loading={loading}
                numberOfInvited={visibleReferees.length}
                referrerName={referrerName}
                referrerAvatarUrl={referrerAvatarUrl ?? fallbackAvatar}
              />
            </div>
          </div>
        </AboutUsSiteContainer>
      </div>
    </div>
  );
};
